import { createFileRoute, Link, useNavigate, retainSearchParams } from "@tanstack/react-router";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { z } from "zod";
import { useState, useMemo } from "react";
import { useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  listOrders,
  listCustomerSkus,
  createOrder,
  importOrdersCsv,
} from "@/lib/operations.functions";
import { allocateOrder } from "@/lib/orders.functions";
import { transitionOrderStatus } from "@/lib/orders-edit.functions";
import {
  listOrderOperationalSummaries,
  type OrderSummaryLite,
  type OrderHealth,
} from "@/lib/orders/summary.functions";
import { runServerFn } from "@/lib/server-fn";
import { useScope } from "@/hooks/use-scope";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Loader2, Upload } from "lucide-react";
import { DataTable, type ColumnDef } from "@/components/ui/data-table";
import { ExportMenu } from "@/components/ui/export-menu";
import { FilterBar } from "@/components/ui/filter-bar";
import { StatusPill } from "@/components/ui/status-pill";
import { toneFromHealth } from "@/lib/health-tone";
import { PageHeader } from "@/components/ui/page-header";
import { ScopeBar } from "@/components/scope-bar";
import { HelpButton } from "@/components/kb/help-button";
import { OrderRef } from "@/components/orders/order-ref";
import { ContextualTip } from "@/components/kb/contextual-tip";
import { BulkActionBar } from "@/components/ui/bulk-action-bar";
import { ViewChips, type ViewChip } from "@/components/ui/view-chips";
import { toast } from "sonner";
import { DateRangeFilter } from "@/components/filters/date-range-filter";
import { DateFieldSelect } from "@/components/filters/date-field-select";
import { SortDirectionToggle } from "@/components/filters/sort-direction-toggle";
import { dateRangeSearchSchema, dateRangeToBounds, retainDateRangeKeys } from "@/lib/date-range-search";

const ordersSearchSchema = dateRangeSearchSchema.extend({
  dateField: fallback(
    z.enum(["created_at", "order_date", "requested_ship_date"]).optional(),
    undefined,
  ),
});

// Health color system — Sprint 36 M3. The Summary supplies the semantic
// health value from order_assessments; this map is presentation-only.
const HEALTH_STYLE: Record<OrderHealth, { dot: string; label: string }> = {
  ok:      { dot: "bg-emerald-500",  label: "On track" },
  watch:   { dot: "bg-amber-500",    label: "Watch" },
  urgent:  { dot: "bg-orange-600",   label: "Urgent" },
  blocked: { dot: "bg-red-600",      label: "Blocked" },
};

function HealthDot({ health, reason }: { health: OrderHealth | undefined; reason: string | null }) {
  const h = (health ?? "ok") as OrderHealth;
  const style = HEALTH_STYLE[h];
  const title = reason ? `${style.label} — ${reason}` : style.label;
  return (
    <span
      className={`inline-block h-2 w-2 rounded-full ${style.dot}`}
      title={title}
      aria-label={title}
    />
  );
}

function formatAge(ms: number | undefined): string {
  if (ms == null || !Number.isFinite(ms) || ms < 0) return "—";
  const min = Math.floor(ms / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d`;
  const mo = Math.floor(d / 30);
  return `${mo}mo`;
}

export const Route = createFileRoute("/_authenticated/app/orders/")({
  validateSearch: zodValidator(ordersSearchSchema),
  search: { middlewares: [retainSearchParams([...retainDateRangeKeys])] },
  component: OrdersListPage,
});

type OrderRow = {
  id: string; order_number: string; status: string;
  carrier: string | null; customer_id: string; created_at: string;
};

function OrdersListPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const urlSearch = Route.useSearch();
  type UrlSearch = typeof urlSearch;
  const navigateSearch = useNavigate({ from: Route.fullPath });
  const dateField = (urlSearch.dateField ?? "created_at") as "created_at" | "order_date" | "requested_ship_date";
  const sort = urlSearch.sort ?? "desc";
  const { fromIso, toIso } = useMemo(
    () => dateRangeToBounds({ start: urlSearch.start, end: urlSearch.end }),
    [urlSearch.start, urlSearch.end],
  );
  const { tenantId, facilityId, customerId, customers, facilities, tenants } = useScope();
  const tenantCustomers = useMemo(() => customers.filter((c) => c.tenant_id === tenantId), [customers, tenantId]);
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importCustomer, setImportCustomer] = useState<string>("");
  const [importText, setImportText] = useState("");
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{ created: number; skipped: number; errors: string[] } | null>(null);
  const [orderNumber, setOrderNumber] = useState("");
  const [carrier, setCarrier] = useState("");
  const [service, setService] = useState("");
  const [shipName, setShipName] = useState("");
  const [shipAddr, setShipAddr] = useState("");
  const [formCustomer, setFormCustomer] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  const [draftLines, setDraftLines] = useState<{ sku_id: string; qty: string }[]>([{ sku_id: "", qty: "" }]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [view, setView] = useState<"all" | "unallocated" | "backordered" | "held" | "shipped_today">("all");
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  const [allocating, setAllocating] = useState(false);
  const [releasing, setReleasing] = useState(false);

  const fetchSkus = useServerFn(listCustomerSkus);
  const fetchOrders = useServerFn(listOrders);
  const createOrderFn = useServerFn(createOrder);
  const importOrdersFn = useServerFn(importOrdersCsv);
  const allocateOrderFn = useServerFn(allocateOrder);
  const transitionFn = useServerFn(transitionOrderStatus);
  const fetchSummaries = useServerFn(listOrderOperationalSummaries);

  const activeCustomer = customerId ?? formCustomer;
  const { data: formSkus = [] } = useQuery({
    queryKey: ["skus-for-new-order", tenantId, activeCustomer],
    enabled: open && !!tenantId && !!activeCustomer,
    queryFn: () => fetchSkus({ data: { customerId: activeCustomer!, activeOnly: true } }),
  });

  const { data, isLoading } = useQuery({
    queryKey: ["orders", tenantId, facilityId, customerId, search, fromIso, toIso, dateField, sort],
    enabled: !!tenantId && !!facilityId,
    queryFn: () => fetchOrders({
      data: {
        tenantId: tenantId!,
        facilityId: facilityId!,
        customerId: customerId ?? null,
        search: search.trim().length >= 2 ? search.trim() : null,
        fromIso, toIso, dateField, sort,
      },
    }),
    placeholderData: keepPreviousData,
  });

  const customerName = (id: string) => customers.find((c) => c.id === id)?.name ?? "—";

  // ---------------------------------------------------------------------------
  // Operational Summary — Sprint 36 M3.
  // The row layer MUST consume from the Summary only. This query is the sole
  // source of health, next-action, customer, and lines/items for row cells.
  // If a row needs a field not present here, escalate — do not re-derive.
  // ---------------------------------------------------------------------------
  const baseOrderIds = useMemo(() => (data ?? []).map((o) => o.id), [data]);
  const { data: summaries } = useQuery({
    queryKey: ["order-summaries", baseOrderIds],
    enabled: baseOrderIds.length > 0,
    queryFn: () => fetchSummaries({ data: { orderIds: baseOrderIds } }),
    placeholderData: keepPreviousData,
  });
  const summaryById = useMemo(() => {
    const m = new Map<string, OrderSummaryLite>();
    for (const s of (summaries ?? []) as OrderSummaryLite[]) m.set(s.identity.id, s);
    return m;
  }, [summaries]);

  const ALLOCATABLE = new Set(["open", "pending_allocation", "backordered"]);
  const selectedIds = useMemo(
    () => Object.entries(rowSelection).filter(([, v]) => v).map(([k]) => k),
    [rowSelection],
  );
  const selectedAllocatable = useMemo(() => {
    if (!data) return [] as string[];
    const map = new Map(data.map((o) => [o.id, o.status] as const));
    return selectedIds.filter((id) => ALLOCATABLE.has(map.get(id) ?? ""));
  }, [selectedIds, data]);

  const selectedDrafts = useMemo(() => {
    if (!data) return [] as string[];
    const map = new Map(data.map((o) => [o.id, o.status] as const));
    return selectedIds.filter((id) => map.get(id) === "draft");
  }, [selectedIds, data]);

  const bulkAllocate = async () => {
    if (selectedAllocatable.length === 0) return;
    setAllocating(true);
    const results = await Promise.allSettled(
      selectedAllocatable.map((orderId) =>
        runServerFn(() => allocateOrderFn({ data: { orderId } }), "Allocate order"),
      ),
    );
    const ok = results.filter((r) => r.status === "fulfilled").length;
    const failed = results.length - ok;
    if (ok > 0) toast.success(`Allocated ${ok} order${ok > 1 ? "s" : ""}`);
    if (failed > 0) toast.error(`${failed} failed to allocate`);
    setRowSelection({});
    setAllocating(false);
    qc.invalidateQueries({ queryKey: ["orders"] });
  };

  const bulkRelease = async () => {
    if (selectedDrafts.length === 0) return;
    setReleasing(true);
    const results = await Promise.allSettled(
      selectedDrafts.map((orderId) =>
        runServerFn(
          () => transitionFn({ data: { orderId, to: "open" } }),
          "Release order",
        ),
      ),
    );
    const ok = results.filter(
      (r) => r.status === "fulfilled" && (r.value as { ok?: boolean })?.ok !== false,
    ).length;
    const failed = results.length - ok;
    if (ok > 0) toast.success(`Released ${ok} draft${ok > 1 ? "s" : ""} to Open`);
    if (failed > 0) toast.error(`${failed} failed to release`);
    setRowSelection({});
    setReleasing(false);
    qc.invalidateQueries({ queryKey: ["orders"] });
  };

  const handleNoSetup = () => {
    if (!tenants.length) {
      toast.error("Create a tenant first", { action: { label: "Go", onClick: () => navigate({ to: "/app/admin/tenants" }) } });
    } else if (!facilities.filter((f) => f.tenant_id === tenantId).length) {
      toast.error("Create a facility first", { action: { label: "Go", onClick: () => navigate({ to: "/app/admin/facilities" }) } });
    } else {
      toast.error("Select a facility in the scope bar above");
    }
  };

  const filtered = useMemo(() => {
    if (!data) return [];
    const startOfToday = new Date(); startOfToday.setHours(0, 0, 0, 0);
    return data.filter((o) => {
      // Saved view
      if (view === "unallocated" && !["open", "pending_allocation"].includes(o.status)) return false;
      if (view === "backordered" && o.status !== "backordered") return false;
      if (view === "held" && !["on_hold", "exception"].includes(o.status)) return false;
      if (view === "shipped_today") {
        if (o.status !== "shipped" && o.status !== "partially_shipped") return false;
        if (new Date(o.created_at) < startOfToday) return false;
      }
      // Status select still applies
      if (statusFilter !== "all" && o.status !== statusFilter) return false;
      if (!search) return true;
      const term = search.toLowerCase();
      return (
        o.order_number.toLowerCase().includes(term) ||
        (o.carrier ?? "").toLowerCase().includes(term) ||
        customerName(o.customer_id).toLowerCase().includes(term)
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, search, statusFilter, view, customers]);

  // View counts (ignore search + status filter so chips reflect dataset)
  const viewCounts = useMemo(() => {
    const rows = data ?? [];
    const startOfToday = new Date(); startOfToday.setHours(0, 0, 0, 0);
    return {
      all: rows.length,
      unallocated: rows.filter((o) => ["open", "pending_allocation"].includes(o.status)).length,
      backordered: rows.filter((o) => o.status === "backordered").length,
      held: rows.filter((o) => ["on_hold", "exception"].includes(o.status)).length,
      shipped_today: rows.filter((o) =>
        ["shipped", "partially_shipped"].includes(o.status) && new Date(o.created_at) >= startOfToday,
      ).length,
    };
  }, [data]);

  const VIEWS: ViewChip<typeof view>[] = [
    { id: "all", label: "All", count: viewCounts.all },
    { id: "unallocated", label: "Unallocated", count: viewCounts.unallocated },
    { id: "backordered", label: "Backordered", count: viewCounts.backordered },
    { id: "held", label: "Held", count: viewCounts.held },
    { id: "shipped_today", label: "Shipped today", count: viewCounts.shipped_today },
  ];

  const buildExportRows = () =>
    filtered.map((o) => ({
      order_number: o.order_number,
      customer: customerName(o.customer_id),
      carrier: o.carrier ?? "",
      status: o.status,
      created_at: new Date(o.created_at).toISOString(),
    }));

  const importCsv = async () => {
    if (!tenantId || !facilityId) return;
    const cid = customerId ?? importCustomer;
    if (!cid) return toast.error("Select a customer");
    const text = importText.trim();
    if (!text) return toast.error("Paste CSV content");
    setImporting(true);
    setImportResult(null);

    const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
    const header = lines[0].split(",").map((h) => h.trim().toLowerCase());
    const required = ["order_number", "sku", "qty"];
    for (const r of required) {
      if (!header.includes(r)) {
        setImporting(false);
        return toast.error(`Missing required column: ${r}`);
      }
    }
    const idx = (k: string) => header.indexOf(k);
    const rows = lines.slice(1).map((ln) => {
      const cells = ln.split(",").map((c) => c.trim());
      return {
        order_number: cells[idx("order_number")],
        sku: cells[idx("sku")],
        qty: parseInt(cells[idx("qty")] ?? "0", 10),
        carrier: idx("carrier") >= 0 ? cells[idx("carrier")] : undefined,
        service: idx("service") >= 0 ? cells[idx("service")] : undefined,
        ship_name: idx("ship_name") >= 0 ? cells[idx("ship_name")] : undefined,
        ship_address: idx("ship_address") >= 0 ? cells[idx("ship_address")] : undefined,
      };
    }).filter((r) => r.order_number && r.sku && r.qty > 0);

    try {
      const result = await runServerFn(
        () => importOrdersFn({ data: { tenantId, facilityId, customerId: cid, rows } }),
        "Import orders",
      );
      setImportResult(result);
      if (result.created > 0) {
        toast.success(`${result.created} order${result.created > 1 ? "s" : ""} imported`);
        qc.invalidateQueries({ queryKey: ["orders"] });
      }
    } catch {
      // toast handled
    } finally {
      setImporting(false);
    }
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const cid = customerId ?? formCustomer;
    if (!tenantId || !facilityId || !cid) return toast.error("Pick tenant, facility, and customer");
    const lines = draftLines
      .map((l) => ({ skuId: l.sku_id, qty: parseInt(l.qty, 10) }))
      .filter((l) => l.skuId && l.qty > 0);
    setSubmitting(true);
    try {
      await runServerFn(
        () => createOrderFn({
          data: {
            tenantId,
            facilityId,
            customerId: cid,
            orderNumber: orderNumber.trim(),
            carrier: carrier.trim() || null,
            serviceLevel: service.trim() || null,
            shipName: shipName.trim() || null,
            shipAddress: shipAddr.trim() || null,
            lines,
          },
        }),
        "Create order",
      );
      toast.success(lines.length > 0 ? `Order created with ${lines.length} line${lines.length > 1 ? "s" : ""}` : "Order created");
      setOpen(false);
      setOrderNumber(""); setCarrier(""); setService(""); setShipName(""); setShipAddr(""); setFormCustomer("");
      setDraftLines([{ sku_id: "", qty: "" }]);
      qc.invalidateQueries({ queryKey: ["orders"] });
    } catch {
      // toast handled
    } finally {
      setSubmitting(false);
    }
  };

  const cols: ColumnDef<OrderRow, unknown>[] = [
    // Health dot — Summary.row.health only. No local health derivation.
    { id: "health", header: "", enableSorting: false,
      cell: ({ row }) => {
        const s = summaryById.get(row.original.id);
        return <HealthDot health={s?.row.health} reason={s?.hover.health_reason.message ?? null} />;
      } },
    // Identity — Summary.identity.order_number.
    { id: "order_number", header: "Order #", accessorKey: "order_number",
      cell: ({ row }) => {
        const s = summaryById.get(row.original.id);
        return (
          <OrderRef
            summary={s}
            orderId={row.original.id}
            fallbackOrderNumber={row.original.order_number}
          />
        );
      } },
    // Status — Summary.row.status (state machine authoritative).
    { id: "status", header: "Status", accessorKey: "status",
      cell: ({ row }) => {
        const s = summaryById.get(row.original.id);
        const status = s?.row.status ?? row.original.status;
        // Sprint 36 closeout — tone sourced from order_assessments.health, not statusTone regex.
        return <StatusPill tone={toneFromHealth(s?.row.health)} dot>{status}</StatusPill>;
      } },
    // Next action — Summary.row.next_action.label. Blank when the owning
    // system has nothing to say (no fabrication).
    { id: "next_action", header: "Next action", enableSorting: false,
      cell: ({ row }) => {
        const s = summaryById.get(row.original.id);
        const label = s?.row.next_action.label;
        return label
          ? <span className="text-sm">{label}</span>
          : <span className="text-xs text-muted-foreground">—</span>;
      } },
    // Customer — Summary.row.customer.name.
    { id: "customer", header: "Customer", enableSorting: false,
      cell: ({ row }) => {
        const s = summaryById.get(row.original.id);
        return <span className="text-muted-foreground text-sm">{s?.row.customer.name ?? "—"}</span>;
      } },
    // Lines / Items — Summary.row.lines / .items.
    { id: "lines_items", header: "Lines/Items", enableSorting: false,
      cell: ({ row }) => {
        const s = summaryById.get(row.original.id);
        if (!s) return <span className="text-xs text-muted-foreground">—</span>;
        return <span className="text-sm tabular-nums text-muted-foreground">{s.row.lines}/{s.row.items}</span>;
      } },
    // Age since created — Summary.row.age_since_created_ms.
    { id: "age", header: "Age", enableSorting: false,
      cell: ({ row }) => {
        const s = summaryById.get(row.original.id);
        return <span className="text-muted-foreground text-sm">{formatAge(s?.row.age_since_created_ms)}</span>;
      } },
  ];

  return (
    <>
      <PageHeader
        title="Orders"
        description="Inbound sales orders ready to allocate, pick, pack, and ship."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <ScopeBar />
            <HelpButton moduleKey="orders" />
            {tenantId ? (
              <ExportMenu
                entity="orders"
                tenantId={tenantId}
                facilityId={facilityId}
                customerId={customerId}
                getRows={buildExportRows}
                columns={["order_number", "customer", "carrier", "status", "created_at"]}
                filters={{
                  view,
                  dateField,
                  start: urlSearch.start,
                  end: urlSearch.end,
                  sort,
                }}
                disabled={filtered.length === 0}
              />
            ) : null}
        <Dialog open={importOpen} onOpenChange={(v) => {
          if (v && !facilityId) { handleNoSetup(); return; }
          setImportOpen(v); if (!v) { setImportText(""); setImportResult(null); }
        }}>
          <DialogTrigger asChild>
            <Button variant="outline" size="sm"><Upload className="h-4 w-4 mr-2" />Import</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>Import orders from CSV</DialogTitle></DialogHeader>
            <div className="space-y-3">
              {!customerId && (
                <div className="space-y-2"><Label>Customer</Label>
                  <Select value={importCustomer} onValueChange={setImportCustomer}>
                    <SelectTrigger><SelectValue placeholder="Select customer" /></SelectTrigger>
                    <SelectContent>{tenantCustomers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-2">
                <Label>CSV content</Label>
                <p className="text-xs text-muted-foreground">
                  Required columns: <code className="font-mono">order_number, sku, qty</code>. Optional: <code className="font-mono">carrier, service, ship_name, ship_address</code>. Multiple lines with the same <code>order_number</code> are grouped into one order.
                </p>
                <textarea
                  value={importText}
                  onChange={(e) => setImportText(e.target.value)}
                  rows={10}
                  className="w-full font-mono text-xs border rounded-md p-2 bg-background"
                  placeholder={`order_number,sku,qty,carrier,service,ship_name,ship_address\nSO-1001,WIDGET-A,2,UPS,Ground,Acme Inc,123 Main St\nSO-1001,WIDGET-B,1,UPS,Ground,Acme Inc,123 Main St\nSO-1002,WIDGET-A,5,USPS,Priority,Beta LLC,456 Oak Ave`}
                />
              </div>
              {importResult && (
                <div className="text-sm space-y-1 border rounded-md p-3 bg-muted/40">
                  <div>Created: <span className="font-medium">{importResult.created}</span></div>
                  <div>Skipped: <span className="font-medium">{importResult.skipped}</span></div>
                  {importResult.errors.length > 0 && (
                    <div className="text-destructive max-h-32 overflow-auto">
                      {importResult.errors.map((e, i) => <div key={i} className="text-xs">{e}</div>)}
                    </div>
                  )}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button onClick={importCsv} disabled={importing}>
                {importing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Import
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <Dialog open={open} onOpenChange={(v) => { if (v && !facilityId) { handleNoSetup(); return; } setOpen(v); }}>
          <DialogTrigger asChild><Button size="sm"><Plus className="h-4 w-4 mr-2" />New order</Button></DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>Create Order</DialogTitle></DialogHeader>
            <form onSubmit={onSubmit} className="space-y-4">
              {!customerId && (
                <div className="space-y-2"><Label>Customer</Label>
                  <Select value={formCustomer} onValueChange={setFormCustomer}>
                    <SelectTrigger><SelectValue placeholder="Select customer" /></SelectTrigger>
                    <SelectContent>{tenantCustomers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-2"><Label>Order number</Label><Input required value={orderNumber} onChange={(e) => setOrderNumber(e.target.value)} placeholder="SO-1001" /></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Carrier</Label><Input value={carrier} onChange={(e) => setCarrier(e.target.value)} placeholder="UPS" /></div>
                <div className="space-y-2"><Label>Service</Label><Input value={service} onChange={(e) => setService(e.target.value)} placeholder="Ground" /></div>
              </div>
              <div className="space-y-2"><Label>Ship to name</Label><Input value={shipName} onChange={(e) => setShipName(e.target.value)} /></div>
              <div className="space-y-2"><Label>Ship to address</Label><Input value={shipAddr} onChange={(e) => setShipAddr(e.target.value)} /></div>
              <div className="space-y-2 border-t pt-4">
                <div className="flex items-center justify-between">
                  <Label>Lines (optional)</Label>
                  <Button type="button" size="sm" variant="ghost" onClick={() => setDraftLines((ls) => [...ls, { sku_id: "", qty: "" }])}>
                    <Plus className="h-3 w-3 mr-1" />Add line
                  </Button>
                </div>
                {(customerId ?? formCustomer) ? (
                  <div className="space-y-2 max-h-56 overflow-auto">
                    {draftLines.map((l, i) => (
                      <div key={i} className="flex gap-2">
                        <Select value={l.sku_id} onValueChange={(v) => setDraftLines((ls) => ls.map((x, idx) => idx === i ? { ...x, sku_id: v } : x))}>
                          <SelectTrigger className="flex-1"><SelectValue placeholder="Select SKU" /></SelectTrigger>
                          <SelectContent>{formSkus.map((s) => <SelectItem key={s.id} value={s.id}>{s.sku_code} — {s.name}</SelectItem>)}</SelectContent>
                        </Select>
                        <Input type="number" min={1} placeholder="Qty" className="w-24" value={l.qty}
                          onChange={(e) => setDraftLines((ls) => ls.map((x, idx) => idx === i ? { ...x, qty: e.target.value } : x))} />
                        <Button type="button" size="sm" variant="ghost" onClick={() => setDraftLines((ls) => ls.length > 1 ? ls.filter((_, idx) => idx !== i) : [{ sku_id: "", qty: "" }])}>×</Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">Select a customer to add lines.</p>
                )}
              </div>
              <DialogFooter><Button type="submit" disabled={submitting}>{submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Create</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
          </div>
        }
      />
      <div className="px-4 sm:px-6 py-4 space-y-4">
        <ContextualTip tipKey="orders_empty" />
        <ViewChips views={VIEWS} value={view} onChange={setView} />
        <FilterBar search={search} onSearchChange={setSearch} searchPlaceholder="Search order #, customer, carrier…">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="pending_allocation">Pending allocation</SelectItem>
              <SelectItem value="allocated">Allocated</SelectItem>
              <SelectItem value="backordered">Backordered</SelectItem>
              <SelectItem value="picking">Picking</SelectItem>
              <SelectItem value="picked">Picked</SelectItem>
              <SelectItem value="packed">Packed</SelectItem>
              <SelectItem value="ready_to_ship">Ready to ship</SelectItem>
              <SelectItem value="partially_shipped">Partially shipped</SelectItem>
              <SelectItem value="shipped">Shipped</SelectItem>
              <SelectItem value="on_hold">On hold</SelectItem>
              <SelectItem value="exception">Exception</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
          <DateFieldSelect
            value={dateField}
            onChange={(v) =>
              navigateSearch({ search: (p: UrlSearch) => ({ ...p, dateField: v === "created_at" ? undefined : (v as UrlSearch["dateField"]) }) })
            }
            options={[
              { value: "created_at", label: "Created" },
              { value: "order_date", label: "Order date" },
              { value: "requested_ship_date", label: "Req. ship" },
            ]}
          />
          <DateRangeFilter
            value={{ start: urlSearch.start, end: urlSearch.end }}
            onChange={(next) => navigateSearch({ search: (p: UrlSearch) => ({ ...p, start: next.start, end: next.end }) })}
          />
          <SortDirectionToggle
            value={sort}
            onChange={(s) => navigateSearch({ search: (p: UrlSearch) => ({ ...p, sort: s === "desc" ? undefined : s }) })}
          />
          {(urlSearch.start || urlSearch.end || urlSearch.dateField || urlSearch.sort) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigateSearch({ search: () => ({}) })}
            >
              Clear
            </Button>
          )}
        </FilterBar>
        <DataTable
          data={filtered as OrderRow[]}
          columns={cols}
          isLoading={isLoading}
          getRowId={(r) => r.id}
          selectable
          rowSelection={rowSelection}
          onRowSelectionChange={setRowSelection}
          empty={{ title: data && data.length > 0 ? "No matches" : "No orders yet",
            description: data && data.length > 0 ? "Try adjusting search or status filter." : "Create your first order or import a CSV." }}
          mobileCard={(o) => {
            // Mobile row: Summary is the only source. Identical hierarchy to desktop row.
            const s = summaryById.get(o.id);
            return (
              <Link to="/app/orders/$orderId" params={{ orderId: o.id }} className="block space-y-1.5">
                <div className="flex items-center gap-2">
                  <HealthDot health={s?.row.health} reason={s?.hover.health_reason.message ?? null} />
                  <span className="font-medium font-mono flex-1">{s?.identity.order_number ?? o.order_number}</span>
                  <StatusPill tone={toneFromHealth(s?.row.health)} dot>{s?.row.status ?? o.status}</StatusPill>
                </div>
                {s?.row.next_action.label && (
                  <div className="text-xs">{s.row.next_action.label}</div>
                )}
                <div className="text-xs text-muted-foreground truncate">
                  {s?.row.customer.name ?? "—"} · {s?.row.lines ?? 0}/{s?.row.items ?? 0} · {formatAge(s?.row.age_since_created_ms)}
                </div>
              </Link>
            );
          }}
        />
      </div>
      <BulkActionBar count={selectedIds.length} onClear={() => setRowSelection({})}>
        <span className="text-xs text-muted-foreground">
          {selectedDrafts.length} draft · {selectedAllocatable.length} allocatable
        </span>
        <Button
          size="sm"
          variant="outline"
          onClick={bulkRelease}
          disabled={releasing || selectedDrafts.length === 0}
        >
          {releasing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          Release to Open
        </Button>
        <Button
          size="sm"
          onClick={bulkAllocate}
          disabled={allocating || selectedAllocatable.length === 0}
        >
          {allocating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          Allocate
        </Button>
      </BulkActionBar>
    </>
  );
}